import { Head } from "@/components/base/Head";
import React from "react";
import { LpLayout } from "@/layouts/header-footer-none";
import { personalities } from "@/data/animal_personalities";
import { Personality } from "@/types/personality";
import Image from "next/image";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { useToast } from "@/utils";
import { ArrowRightIcon } from "@heroicons/react/24/solid";

const ResultPage = ({ personality }: { personality: Personality }) => {
  const META_TITLE = `${personality.name}`;
  const URL = `https://app.s-d-lab.jp/animal_diagnosis/results/${personality.id}`;
  const toast = useToast();

  const shares = [
    {
      image: "share_x.png",
      alt: "Xにシェア",
      link: `https://twitter.com/intent/tweet?text=私の仕事タイプは${personality.name}です！&url=${URL}&hashtags=中小企業の戦略ラボ,仕事タイプ診断`,
    },
    {
      image: "share_facebook.png",
      alt: "Facebookにシェア",
      link: `https://www.facebook.com/sharer/sharer.php?u=${URL}`,
    },
    {
      image: "share_line.png",
      alt: "LINEにシェア",
      link: `https://social-plugins.line.me/lineit/share?url=${URL}`,
    },
  ];

  const onSubmit = async (data: any) => {
    try {
      await navigator.clipboard.writeText(data.url);
      toast.success(`URLをコピーしました`);
    } catch (e) {
      console.log(e);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  return (
    <>
      <Head
        title={META_TITLE}
        description={personality.description}
        canonicalPath={`/animal_diagnosis/results/${personality.id}`}
        image={`https://app.s-d-lab.jp/images/animal_diagnosis/ogp/${personality.type.toLowerCase()}.jpg`}
      />
      <main className={"bg-[#F6F8FA] pt-10 pb-[120px]"}>
        <div className={"max-w-[420px] m-auto bg-white"}>
          <div className={`bg-[#e9536c] text-center text-white py-8 relative`}>
            <span
              className={`bg-[#e9536c] absolute h-[140px] w-full left-0 bottom-[-40px] skew-y-6`}
            />
            <div className={"relative"}>
              <h1 className={"text-[40px] font-bold"}>{personality.name}</h1>
              <p className={"font-bold mb-3"}>型の仕事タイプ</p>
            </div>
          </div>
          <div className={"mb-4 relative"}>
            <Image
              src={`/images/animal_diagnosis/${personality.type.toLowerCase()}.png`}
              width={420}
              height={420}
              alt={""}
              loading={"eager"}
            />
          </div>
          <div className={"p-6"}>
            <div className={"mb-10"}>
              <h2 className={"font-bold text-3xl mb-3"}>性格</h2>
              <p className={"text-lg leading-[1.75]"}>
                {personality.description}
              </p>
            </div>
            <div className={"mb-10"}>
              <h2 className={"font-bold text-3xl mb-3"}>強み</h2>
              <p className={"text-lg leading-[1.75]"}>
                {personality.strongPoint}
              </p>
            </div>
            <div className={"mb-10"}>
              <h2 className={"font-bold text-3xl mb-3"}>弱み</h2>
              <p className={"text-lg leading-[1.75]"}>
                {personality.weakPoint}
              </p>
            </div>
            <div className={"text-center mb-10"}>
              {shares.map((share, index) => (
                <a
                  key={index}
                  href={share.link}
                  target={"_blank"}
                  rel="noreferrer"
                  className={
                    "hover:opacity-70 duration-500 leading-[0] inline-block mb-2"
                  }
                >
                  <Image
                    width={300}
                    height={60}
                    alt={share.alt}
                    src={`/images/animal_diagnosis/${share.image}`}
                    loading={"eager"}
                  />
                </a>
              ))}
              <div className={"mt-2"}>
                <form onSubmit={handleSubmit((data) => onSubmit(data))}>
                  <input
                    type="text"
                    value={URL}
                    className={
                      "w-full py-3 px-3 border border-[#ccc] w-[300px] mx-auto mb-2 text-sm"
                    }
                    {...register("url")}
                  />
                  <button
                    className={
                      "bg-[#e9536c] text-white rounded px-3 py-2 hover:opacity-70 duration-500 font-bold"
                    }
                    type="submit"
                  >
                    URLをコピーする
                  </button>
                </form>
              </div>
            </div>
            <div>
              <Link
                href={`/animal_diagnosis`}
                className={
                  "text-white flex items-center justify-between bg-[#e9536c] text-xl w-[240px] py-4 px-6 m-auto font-bold rounded-full hover:opacity-70 duration-500"
                }
              >
                <ArrowRightIcon className={"w-5"} />
                もう一度診断する
              </Link>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

ResultPage.Layout = LpLayout;
export function getStaticPaths() {
  const paths = personalities.map((personality) => ({
    params: { id: personality.id },
  }));
  return { paths, fallback: false };
}
export function getStaticProps(context: { params: { id: string } }) {
  const response = personalities.find((personality) => {
    return personality.id === context.params.id;
  });
  if (response) {
    return {
      props: {
        personality: response,
      },
    };
  } else {
    return {
      redirect: {
        destination: `/404`,
        permanent: false,
      },
    };
  }
}
export default ResultPage;
